export type AISystem = {
  id: string;
  name: string;
  entity: AISystemEntity;
  organizationId: string;
  organization: string | null;
  description: string | null;
  version: string;
  riskClassification: RiskClassification | null;
  complianceStatus: ComplianceStatus;
  assessmentType?: AssessmentType;
  complianceContact: string | null;
  accountId: string;
  ceMarkUnlocked: boolean;
  createdAt: string;
  updatedAt: string;
  origin: AISystemOrigin;
  createdById: string; // ID of the user who created the system
  needs_delegation: boolean;
  owner: Owner;
};

export type AISystemsResponse = {
  items?: AISystem[];
  total?: number;
};

export enum RiskClassification {
  HIGH_RISK = 'HIGH_RISK',
  HIGH_RISK_EXCEPTION = 'HIGH_RISK_EXCEPTION',
  PROHIBITED = 'PROHIBITED',
  LIMITED_RISK = 'LIMITED_RISK',
  MINIMAL_RISK = 'MINIMAL_RISK',
  AUTHORISED = 'AUTHORISED_REPRESENTATIVE',
  OUT_OF_SCOPE = 'OUT_OF_SCOPE',
  EXCLUDED = 'EXCLUDED',
  PRODUCT_MANUFACTURER = 'PRODUCT_MANUFACTURER',
  OTHER = 'OTHER'
}

export enum AISystemEntity {
  PROVIDER = 'PROVIDER',
  DEPLOYER = 'DEPLOYER',
  DISTRIBUTOR = 'DISTRIBUTOR',
  IMPORTER = 'IMPORTER',
  PRODUCT_MANUFACTURER = 'PRODUCT_MANUFACTURER',
  AUTHORISED_REPRESENTATIVE = 'AUTHORISED_REPRESENTATIVE'
}

export enum ComplianceStatus {
  INITIATED = 'INITIATED',
  CLASSIFYING = 'CLASSIFYING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLIANT = 'COMPLIANT',
  COMPLIANT_RISK_CLASSIFIED = 'COMPLIANT_RISK_CLASSIFIED',
  NON_COMPLIANT = 'NON_COMPLIANT',
  ATTENTION_REQUIRED = 'ATTENTION_REQUIRED'
}

export type AssessmentType = 'QUESTIONNAIRE' | 'AI_POWERED';

export type AISystemOrigin = 'MANUAL' | 'BULK_IMPORT';

export type BulkImportTemplateFormat = 'csv' | 'xlsx';

export type Owner = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type ComplianceStatusCount = {
  status: string;
  count: number;
};

export type RiskLevelCount = {
  risk: string;
  count: number;
};

export type AISystemSummaryDto = {
  total: number;
  compliant: number;
  nonCompliant: number;
  byComplianceStatus: ComplianceStatusCount[];
  byRiskLevel: RiskLevelCount[];
};

export type StartAiSystemRequest = {
  name: string;
  version: string;
  description: string;
  organization: string;
  complianceContact: string;
  assessmentType: AssessmentType | null;
  aiSystemId?: string;
};

export type StartAiSystemResponse = {
  aiSystemId: string;
  assessmentType: AssessmentType;
  type: string;
  questionnaireId: string;
  question: Question;
  step: string;
  snapshot: Snapshot;
  status: string;
  questionnaireVersionId: string;
};

export type QuestionAnswer = {
  questionId: string;
  answer: string[];
};

export type NextQuestion = {
  questionnaireId: string;
  type: string;
  question: Question;
  step: string;
  snapshot: Snapshot;
  status: string;
  questionnaireVersionId: string;
};

export type PreviousQuestion = {
  step: string;
  questions: Question[];
  snapshot: Snapshot;
};

export type Question = {
  id: string;
  step: string;
  section: string;
  title: string;
  type: string;
  source: string;
  options: Option[];
  hint?: string;
};

type Option = {
  value: string;
  label: string;
  description?: string;
  result?: string;
  selected?: string[];
  hint?: string;
  next?: string;
  labelHtml?: string;
  effects?: {
    type: string;
    value: string;
  }[];
};

type Snapshot = {
  answers: Record<string, string | string[]>;
  obligations: string[];
  statusChanges: string[];
  tags: string[];
};

export type AISystemOverviewResponse = {
  requirements: {
    currentStep: number;
    totalSteps: number;
    pendingSteps: number;
  };
  documentation: {
    completed: number;
    total: number;
  };
  complianceStatus: string;
  repositories: {
    count: number;
    hasMajor: boolean;
  };
  riskClassification: string | null;
  generalInfo: {
    entityType: string;
    version: string;
    organizationName: string;
    complianceContactPerson: string;
    description: string;
    createdAt: string;
  };
  users: {
    owner: UserInfo[];
    admin: UserInfo[];
    editor: UserInfo[];
    viewer: UserInfo[];
  };
  ceMark: CEMark | null;
};

export type AiSystemUserQuotaCheck = {
  canAdd: boolean;
  remaining: number;
  alreadyInAccount: boolean;
};

export type TransferOwnershipResponse = {
  message: string;
  aiSystemId: string;
  newOwnerId: string;
};

export type UserInfo = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
};

export type CEMark = {
  url: string;
  ceMarkId: Record<string, any>;
  euDeclarationOfConformityId: Record<string, any>;
  notifiedBodyNumber: string;
  issuedOn: string;
  certificateNumber: string;
};

export type ChangeAssessmentRequest = {
  regulation: 'EU_AI_ACT';
  assessmentType: AssessmentType;
  contextTransfer?: boolean;
};

export type ChangeAssessmentToAIResponse = {
  aiSystemId: string;
  assessmentType: 'AI_POWERED';
  message: string;
  jobId: string;
  status: string;
};

type ChangeAssessmentToQuestionnaireResponse = StartAiSystemResponse & {
  assessmentType: 'QUESTIONNAIRE';
  message: string;
};

export type ChangeAssessmentResponse =
  | ChangeAssessmentToAIResponse
  | ChangeAssessmentToQuestionnaireResponse;

export type BulkImportSkippedRow = {
  rowNumber: number;
  name: string;
  version: string;
  reason: string;
  aiSystemId: string;
  complianceStatus: ComplianceStatus;
};

export type BulkImportError = {
  rowNumber: number;
  field: string;
  reason: string;
};

export type BulkImportFailureReport = {
  reportId: string;
  expiresAt: string;
  filename: string;
  contentType: string;
};

export type BulkImportResponse = {
  fileName: string;
  format: BulkImportTemplateFormat;
  totalDataRows: number;
  validRows: number;
  invalidRows: number;
  matchedRows: number;
  unmatchedRows: number;
  createdRows: number;
  updatedRows: number;
  successfulImports: number;
  skippedRows: number;
  skipped: BulkImportSkippedRow[];
  errors: BulkImportError[];
  failureReport: BulkImportFailureReport;
};
